import { cn } from '@/lib/cn';
import { faChevronUp, faPlug } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { TruncatedText } from 'components/TruncatedText';
import { useAppContext } from 'contexts/AppContext';
import PluginIcon from 'pages/scope/PluginIcon';
import { useDatasourceConfigsForWorkspace } from 'queries/hooks/useDatasourceConfigsForWorkspace';
import { useEffect, useState } from 'react';
import { useMatch } from 'react-router';
import { NavLink } from '../NavLink';
import { NavSection } from '../NavSection';
import { AddDatasourceButton } from '../datasources/AddDatasourceButton';
import { OobDashboardTree } from '../datasources/OobDashboardTree';

export const DatasourceSelectWithOOBDashboards: React.FC = () => {
    const { currentWorkspaceID } = useAppContext();
    const { data: linkedDatasources = [], isLoading } = useDatasourceConfigsForWorkspace(currentWorkspaceID, {
        suspense: true
    });
    const [openId, setOpenId] = useState<string | null>(null);

    const overviewMatch = useMatch('datasource/:id');
    const dashboardMatch = useMatch('datasource/:id/dashboard/*');

    const activePluginId = overviewMatch?.params.id || dashboardMatch?.params.id;
    const activeDashboardPath = (dashboardMatch?.params as unknown as { '*': string })?.['*'] ?? '';

    useEffect(() => {
        if (activePluginId) {
            setOpenId(activePluginId);
        }
    }, [activePluginId, activeDashboardPath]);

    return (
        <NavSection
            title='Data Sources'
            data-testid='navbar-menu-datasources'
            action={<AddDatasourceButton />}
            className='pr-0.5 ml-[2px] !mt-0' // Reduce margin-top to allow space for ghost dashboard folder item
            icon={faPlug}
            iconClassName='ml-px'
        >
            <div className={cn(!isLoading && linkedDatasources.length === 0 ? 'pt-0' : 'pt-1')}>
                {linkedDatasources.map((plugin) => (
                    <div key={plugin.id} className='group/container'>
                        <Collapsible.Root
                            open={openId === plugin.id}
                            onOpenChange={(isOpen) => setOpenId(isOpen ? plugin.id : null)}
                            className='group/root'
                        >
                            <div className='flex group items-center group-data-[state=open]/root:pb-2 group-data-[state=closed]/root:pb-3.5'>
                                <Collapsible.Trigger className='relative w-[16px] h-[16px] ml-[17px] mr-[7px]'>
                                    {/* Use opacity to show/hide icons here, as using display:none causes any defs
                                        nested in the SVG to disappear for other elements on the screen */}
                                    <PluginIcon
                                        className='absolute top-0 left-0 h-[16px] w-[16px] aspect-square shrink-0 opacity-100 group-hover:opacity-0'
                                        pluginName={plugin.plugin?.name || ''}
                                        pluginId={plugin.plugin?.basePlugin ? plugin.plugin?.pluginId : undefined}
                                    />
                                    <FontAwesomeIcon
                                        icon={faChevronUp}
                                        fixedWidth
                                        className='absolute top-0 left-0 text-[13px] w-[16px] transform rotate-180 group-data-[state=closed]/root:rotate-90 opacity-0 group-hover:opacity-100'
                                    />
                                </Collapsible.Trigger>
                                <NavLink to={`/datasource/${plugin.id}`} className='grow pb-0'>
                                    <TruncatedText
                                        className='mr-auto truncate shrink pl-xxxs'
                                        title={plugin.displayName || ''}
                                    >
                                        {plugin.displayName}
                                    </TruncatedText>
                                </NavLink>
                            </div>
                            <Collapsible.Content>
                                {plugin.plugin?.pluginId && (
                                    <OobDashboardTree pluginId={plugin.plugin?.pluginId} configId={plugin.id} />
                                )}
                            </Collapsible.Content>
                        </Collapsible.Root>
                    </div>
                ))}
            </div>

            {!isLoading && linkedDatasources.length === 0 && <p className='mr-2 pl-[32px]'>No data sources</p>}
        </NavSection>
    );
};
